import cloneDeep from "lodash/cloneDeep";
import {
  SET_WIZARD_BUFFER_ITEMS, SET_WIZARD_COPY_EDGES,
  SET_WIZARD_DRAGGING_GROUP_INDEX,
  SET_WIZARD_FLOW_ACTIVE_MODE,
  UPDATE_WIZARD_NODES_COUNT
} from "../../constants/actionTypes";
import {getNextMaxNumberFromList} from "../../utilities";
import {defaultWizardGroup, defaultWizardNode, WIZARD_GROUP_TYPES} from "./WizardItem/wizardItemInterface";
import {updateWizardList} from "./wizardActions";

export const setWizardFlowActiveMode = (wizardFlowActiveMode) => ({
  type: SET_WIZARD_FLOW_ACTIVE_MODE,
  payload: {wizardFlowActiveMode}
});

export const updateWizardNodesCount = () => ({
  type: UPDATE_WIZARD_NODES_COUNT,
});

export const setWizardDraggingGroupIndex = (wizardDraggingGroupIndex) => ({
  type: SET_WIZARD_DRAGGING_GROUP_INDEX,
  payload: {wizardDraggingGroupIndex}
});

export const setBufferWizardItems = (bufferWizardNodes, bufferWizardEdges, bufferWizardItem) => ({
  type: SET_WIZARD_BUFFER_ITEMS,
  payload: {bufferWizardNodes, bufferWizardEdges, bufferWizardItem}
});

export const setShouldCopyEdges = (shouldCopyEdges) => ({
  type: SET_WIZARD_COPY_EDGES,
  payload: {shouldCopyEdges}
});

/**
 * Returns updated wizardActiveItem which should be passed to redux
 * @param {wizardItemInterface} wizardActiveItem // moment js instance
 * @param {object} featureGroup // filter group or feature group
 * @param {string} nodeType custom, inputs, and so on
 * @param {object} position {x:0,y:0}
 * @returns {wizardItemInterface} wizardActiveItem
 */
export const addNewFlowGroup = (wizardActiveItem, featureGroup, nodeType = "", position = null) => {
  return (dispatch, getState) => {

    const {wizardState} = getState();
    const {wizardFlowActiveMode} = wizardState;

    const nextId = getNextMaxNumberFromList(wizardActiveItem.wizardGroups, "id");
    const nextXPosition = wizardActiveItem.wizardNodes.length === 1
      ? -150
      : Math.max(...wizardActiveItem.wizardNodes.map(node => node.type === "input" ? -150 : node.position.x)) + 350

    const newWizardNode = Object.assign({}, defaultWizardNode, {
      id: "" + nextId,
      // position: { x: -100 + (wizardActiveItem.wizardGroups.length * 400), y: 100 },
      position: position || { x: nextXPosition, y: 150 },
    });

    const newWizardGroup = Object.assign({}, defaultWizardGroup, {
      id: "" + nextId,
      nodeType: nodeType || wizardFlowActiveMode,
      groupName: wizardFlowActiveMode === WIZARD_GROUP_TYPES.filter && !nodeType ? featureGroup.name : featureGroup.groupName,
      filterGroupId: wizardFlowActiveMode === WIZARD_GROUP_TYPES.filter && !nodeType ? featureGroup.id : null,
      scopeImageUrl: featureGroup.customImageUrl, // from project scope
      customDisplayName: featureGroup.customDisplayName,
    });
    if (nodeType === WIZARD_GROUP_TYPES.inputs) {
      newWizardGroup.inputFields = [];
    }

    wizardActiveItem.wizardGroups = [...wizardActiveItem.wizardGroups, newWizardGroup];
    wizardActiveItem.wizardNodes = [...wizardActiveItem.wizardNodes, newWizardNode];
    dispatch(updateWizardList(wizardActiveItem));
    dispatch(updateWizardNodesCount());
    return wizardActiveItem
  }
}


/**
 * clones everything that is selected
 * @param selectedWizardNodes {[wizardNodeInterface]}
 * @param selectedWizardEdges {[wizardEdgeInterface]}
 * @param fromWizardItem {wizardItemInterface} // from
 * @param wizardActiveItem {wizardItemInterface} // to
 */
export const cloneFlowGroupsAndEdges = (selectedWizardNodes, selectedWizardEdges, fromWizardItem, wizardActiveItem) => {
  return (dispatch, getState) => {

    const {wizardState} = getState();
    if (wizardState.wizardPopupOpened || !fromWizardItem || !wizardActiveItem) {
      return false
    }

    // console.log("selectedWizardNodes", selectedWizardNodes);
    // console.log("selectedWizardEdges", selectedWizardEdges);
    // console.log("fromWizardItem", fromWizardItem);
    // console.log("toWizardItem", wizardActiveItem);

    let oldIdNewIdObject = {};

    selectedWizardNodes.forEach(wizardNode => {

      const wizardGroup = fromWizardItem.wizardGroups.find(wizGroup => wizGroup.id === wizardNode.id);
      if (!wizardGroup) {
        return
      }

      const nextId = "" + getNextMaxNumberFromList(wizardActiveItem.wizardGroups, "id");
      oldIdNewIdObject[wizardGroup.id] = nextId;

      const clonedWizardGroup = cloneDeep(wizardGroup);
      clonedWizardGroup.id = nextId;

      const clonedWizardNode = cloneDeep(wizardNode);
      clonedWizardNode.id = nextId;
      clonedWizardNode.position = {
        x: wizardNode.position.x + wizardNode.width + 50, // add some gap
        y: wizardNode.position.y
      }

      wizardNode.selected = false; // make source node not selected // not sure if it is needed
      clonedWizardNode.selected = true; // make new node selected // not sure if it is needed

      wizardActiveItem.wizardGroups = [...wizardActiveItem.wizardGroups, clonedWizardGroup];
      wizardActiveItem.wizardNodes = [...wizardActiveItem.wizardNodes, clonedWizardNode];

      // then we are checking edges that are connected to this node (only in selected edges array, we don't need to clone not selected)
    });

    // if (wizardState.shouldCopyEdges || (wizardActiveItem.uniqueHashId && fromWizardItem && (fromWizardItem.uniqueHashId !== wizardActiveItem.uniqueHashId))) {
    if (wizardState.shouldCopyEdges) {
      selectedWizardEdges.forEach(edge => {
        const clonedEdge = Object.assign({}, edge);
        const splittedId = edge.id.split("-"); // "reactflow__edge-0-1"
        if (!oldIdNewIdObject[edge.target] && !oldIdNewIdObject[edge.source]) {
          return // skip the ones that are not from wizard nodes list
        }
        if (oldIdNewIdObject[edge.target]) {
          clonedEdge.target = oldIdNewIdObject[edge.target];
          clonedEdge.id = splittedId[0] + "-" + splittedId[1] + "-" + oldIdNewIdObject[edge.target]; // "reactflow__edge-0-2"
        }
        if (oldIdNewIdObject[edge.source]) {
          clonedEdge.source = oldIdNewIdObject[edge.source];
          clonedEdge.id = splittedId[0] + "-" + oldIdNewIdObject[edge.source] + "-" + splittedId[2]; // "reactflow__edge-2-3"
        }
        clonedEdge.selected = true; // make new edge selected // not sure if it is needed
        wizardActiveItem.wizardEdges.push(clonedEdge);
      });
      wizardActiveItem.wizardEdges = wizardActiveItem.wizardEdges.map(oldEdge => {
        if (selectedWizardEdges.some(edge => edge.id === oldEdge.id)) {
          oldEdge.selected = false; // make source edge not selected
        }
        return oldEdge
      })
    } else {
      wizardActiveItem.wizardEdges = wizardActiveItem.wizardEdges.map(oldEdge => {
        oldEdge.selected = false; // make source edge not selected
        return oldEdge
      })
    }

    console.log("to be saved wizardActiveItem", wizardActiveItem);

    dispatch(updateWizardList(wizardActiveItem));
    dispatch(updateWizardNodesCount());
    return wizardActiveItem
  }
}
